import { useMetricTemplates } from 'features/app-config';
import { useCreateGoal, useGoals, useUpdateGoal } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useUpdateTasks } from 'features/tasks';
import { useIsFavorite, useToggleFavorite } from 'features/user';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { Paths } from 'shared/routes/paths';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Goal, NewGoal } from 'shared/types/goal';
import { GoalFormFields } from 'shared/types/goal-form';
import { ID } from 'shared/types/id';
import { LimitKeys } from 'shared/types/limit-keys';
import { Task } from 'shared/types/task';
import { FavoriteType } from 'shared/types/user-settings';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { targetIsInput } from 'shared/utils/target-is-input';

import { GoalFormContext, GoalFormContextProps } from './goal-form-context';
import { mapGoalFieldsToGoal } from './map-goal-fields-to-goal';
import { mapGoalToGoalFields } from './map-goal-to-goal-fields';

const loadGoalFormDialog = () => import('shared/dialogs/goal-form');
const GoalFormDialog = React.lazy(async () => ({
  default: (await loadGoalFormDialog()).GoalFormDialog,
}));

export type GoalFormProviderProps = {
  children: React.ReactNode;
};

export const GoalFormProvider: React.FC<GoalFormProviderProps> = ({
  children,
}) => {
  const locale = useI18nLocale();
  const navigate = useNavigate();
  const user = useUser();
  const today = useToday();
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();
  const metricTemplates = useMetricTemplates();

  // useOpenPremiumDialog is part of a higher level context which should be kept in mind (probably different setup needed)
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: uploadImageAllowed } = usePremiumFeatureAllowed(
    LimitKeys.GoalUserPhoto,
    {},
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [goal, setGoal] = useState<Partial<Goal>>();
  const [goalId, setGoalId] = useState<ID>();
  const [tasksToLink, setTasksToLink] = useState<Task[]>([]);

  const { submit: updateTasks } = useUpdateTasks();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const [skipNavigation, setSkipNavigation] = useState(false);
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const { retry, isLoading, error, isSuccess, reset } = useMemo(
    () => (goalId ? update : create),
    [create, goalId, update],
  );
  const isFavorite = useIsFavorite(goalId);
  const toggleFavorite = useToggleFavorite();

  const filteredLifeAreas = useMemo(
    () => lifeAreas.filter(({ archivedAt }) => !archivedAt),
    [lifeAreas],
  );

  const openDialog = useCallback(
    (goal?: Partial<Goal>, id?: ID, skipParams?: boolean, tasks?: Task[]) => {
      setGoalId(id);
      setGoal(
        id ? goal : { ...(goal ?? {}), startDate: goal?.startDate ?? today },
      );
      setSkipNavigation(!!skipParams);
      setTasksToLink(tasks ?? []);
      setDialogOpen(true);
    },
    [today],
  );

  const closeDialog = useCallback(() => {
    reset();
    setGoal(undefined);
    setGoalId(undefined);
    setSkipNavigation(false);
    setDialogOpen(false);
  }, [reset]);

  const submitForm = useCallback(
    async (values: GoalFormFields) => {
      const processedValues = mapGoalFieldsToGoal(values, {
        metricTemplates,
        metricEntries: goal?.metric?.entries ?? [],
      });

      if (!processedValues) {
        return;
      }

      if (goalId) {
        update.submit({ ...processedValues, id: goalId }, true);
        isFavorite !== values.favorite &&
          toggleFavorite(goalId, FavoriteType.Goal);
        return;
      }

      // Calculate task counts from tasksToLink
      const taskCount = tasksToLink.length;
      const completedTaskCount = tasksToLink.filter(
        (t) => t.completedAt,
      ).length;

      const createdGoal = await create.submit(
        {
          ...processedValues,
          taskCount: processedValues.taskCount ?? taskCount,
          completedTaskCount:
            processedValues.completedTaskCount ?? completedTaskCount,
        } as NewGoal,
        {
          lifeArea: filteredLifeAreas.find(
            ({ id }) => id === values.lifeAreaId,
          )!,
        },
      );

      // Link tasks to the new goal
      if (tasksToLink.length) {
        // Calculate goal count changes for old goals (tasks being unlinked)
        const goalCountChanges = new Map<
          ID,
          { taskCount: number; completedTaskCount: number }
        >();

        tasksToLink.forEach((task) => {
          const oldGoalId = task.goalId;
          const isCompleted = !!task.completedAt;

          // Decrement old goal if task was linked to a goal
          if (oldGoalId) {
            const current = goalCountChanges.get(oldGoalId) ?? {
              taskCount: 0,
              completedTaskCount: 0,
            };
            goalCountChanges.set(oldGoalId, {
              taskCount: current.taskCount - 1,
              completedTaskCount:
                current.completedTaskCount - (isCompleted ? 1 : 0),
            });
          }
        });

        // Update old goal counts
        goalCountChanges.forEach((changes, goalId) => {
          if (changes.taskCount !== 0 || changes.completedTaskCount !== 0) {
            updateGoalTaskCount({
              goalId,
              taskCount: changes.taskCount,
              completedTaskCount: changes.completedTaskCount,
            });
          }
        });

        const updates = tasksToLink.map((task) => ({
          id: task.id,
          goalId: createdGoal.id,
          lifeAreaId: null,
        }));
        updateTasks(updates);
      }

      !!values.favorite && toggleFavorite(createdGoal.id, FavoriteType.Goal);

      if (!skipNavigation && !createdGoal.parentIds?.length) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: createdGoal.id }));
      }
    },
    [
      create,
      filteredLifeAreas,
      goal?.metric?.entries,
      goalId,
      isFavorite,
      metricTemplates,
      navigate,
      skipNavigation,
      tasksToLink,
      toggleFavorite,
      update,
      updateGoalTaskCount,
      updateTasks,
    ],
  );

  const initialVals = useMemo(() => {
    const fieldValues = goal ? mapGoalToGoalFields(goal, { locale }) : {};
    // startDate should be today for a new goal
    const startDate = goalId ? fieldValues?.startDate : today;
    return {
      startDate,
      ...fieldValues,
      favorite: isFavorite,
    };
  }, [goal, goalId, locale, isFavorite, today]);

  useEffect(() => {
    if (isSuccess) {
      closeDialog();
    }
  }, [closeDialog, isSuccess]);

  const value = useMemo<GoalFormContextProps>(
    () => ({
      onCreateGoal: (options) => {
        const { tasks, skipNavigation, ...initialValues } = options ?? {};
        openDialog(initialValues, undefined, skipNavigation, tasks);
      },
      onEditGoal: (initialValues, id) => openDialog(initialValues, id),
    }),
    [openDialog],
  );

  useEffect(() => {
    // hack around goal form loading, make sure it is loaded for offline use
    loadGoalFormDialog();
  }, []);

  useModifierKeysDown(
    'KeyN',
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      openDialog();
      e.preventDefault();
    },
    { command: true, option: true },
  );

  return (
    <GoalFormContext.Provider value={value}>
      {children}
      <Suspense>
        <GoalFormDialog
          initialValues={initialVals}
          metricTemplates={metricTemplates}
          lifeAreas={filteredLifeAreas}
          goals={goals}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          open={dialogOpen}
          onClose={closeDialog}
          onSubmit={submitForm}
          onRetry={retry}
          isLoading={isLoading}
          isError={!!error}
          isEdit={!!goalId}
          isPremiumImageUploadAllowed={uploadImageAllowed}
          onPremium={openPremiumDialog}
        />
      </Suspense>
    </GoalFormContext.Provider>
  );
};
